import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'
import withTheme from '../styles/withTheme'
import { stylesClasses, generateKey } from '../../utils/classes'
import { responsiveStyles } from '../../utils/styles'
import theme from '../../constants/theme'

import mediaQueries from '../../constants/mediaQueries'

const COLORS = { ...theme['colors'] }

const variantMapping = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	h6: 'h6',
	body: 'p',
	small: 'small',
	inherit: 'p',
	li: 'li'
}

const baseStyles = {
	body: {
		fontSize: '1rem',
		margin: 0
	},
	small: {
		fontSize: '.75rem',
		margin: 0
	},
	inherit: {},
	hBase: {
		extend: 'medium',
		margin: 0
	},
	h1: {
		extend: 'hBase',
		fontSize: '2.5rem',
		'@media(max-width: 991px)': {
			fontSize: '2.125rem'
		},
		'@media(max-width: 767px)': {
			fontSize: '1.75rem'
		}
	},
	h2: {
		extend: 'hBase',
		fontSize: '2rem'
	},
	h3: {
		extend: 'hBase',
		fontSize: '1.75rem',
		'@media(max-width: 991px)': {
			fontSize: '1.625rem'
		},
		'@media(max-width: 767px)': {
			fontSize: '1.5rem'
		}
	},
	h4: {
		extend: 'hBase',
		fontSize: '1.5rem',
		'@media(max-width: 991px)': {
			fontSize: '1.25rem'
		},
		'@media(max-width: 767px)': {
			fontSize: '1rem'
		}
	},
	h5: {
		extend: 'hBase',
		fontSize: '1.25rem',
		'@media(max-width: 991px)': {
			fontSize: '1.125rem'
		},
		'@media(max-width: 767px)': {
			fontSize: '1rem'
		}
	},
	h6: {
		extend: 'hBase',
		fontSize: '1rem'
	},
	light: {
		fontWeight: 300
	},
	regular: {
		fontWeight: 400
	},
	medium: {
		fontWeight: 500
	},
	left: {
		textAlign: 'left'
	},
	center: {
		textAlign: 'center'
	},
	right: {
		textAlign: 'right'
	},
	gutterBottom: {
		marginBottom: '.5em'
	},
	li: {
		listStyle: 'none',
		'&:before': {
			content: '"\\2022"',
			color: '#dba89b',
			display: 'inline-block',
			marginRight: '.75em'
		}
	}
}

function colorStyles(props) {
	const colorStyles = {
		...(props.color && {
			color: colorVarConversion(props.color)
		}),
		...(!props.color &&
			props.theme === 'dark' && {
			color: colorVarConversion('white')
		})
	}
	return colorStyles
}

const wordBreakStyles = {
	forceWordBreak: {
		overflowWrap: 'break-word',
		wordWrap: 'break-word',
		wordBreak: 'break-word',
		'-ms-hyphens': 'auto',
		'-moz-hyphens': 'auto',
		'-webkit-hyphens': 'auto',
		hyphens: 'auto'
	}
}

/**
 * Return the hex color value correspondent to the given string name if matches,
 * otherwise return de given string value.
 * @param {string} colorName - The name of the color, one from the color variables list.
 * @return {string} - The hex value of the color within the list, or the original input value.
 */
function colorVarConversion(colorName) {
	if (typeof colorName !== 'string') {
		return null
	}

	const colorPairs = new Map(Object.entries(COLORS))
	if (colorPairs.has(colorName)) {
		return COLORS[colorName]
	}
	return colorName
}

function generateVariantStyles(mq, baseStyles) {
	return Object.keys(variantMapping).reduce((acc, variant) => {
		const key = generateKey('-', variant, mq)

		acc[key] = responsiveStyles(mq, baseStyles[variant])

		return acc
	}, {})
}

const styles = {
	...Object.keys(mediaQueries).reduce(
		(styles, mq) => ({
			...styles,
			...generateVariantStyles(mq, baseStyles)
		}),
		{}
	),
	colorStyles: (props) => ({
		...colorStyles(props)
	}),
	...baseStyles,
	...wordBreakStyles
}

const useStyles = createUseStyles(styles, { name: 'Vb-typography' })

const Typography = forwardRef(function Typography(props, ref) {
	const {
		align,
		children,
		className,
		color,
		component,
		contentEditable,
		gutterBottom = false,
		onChange,
		onEnterKey,
		fireChangeOnEnter,
		onBlur,
		paragraph = false,
		variant = 'body',
		weight,
		wordBreak,
		...other
	} = props

	const classes = useStyles(props)

	const Component =
		component || (paragraph ? 'p' : variantMapping[variant?.sm || variant?.md || variant?.lg || variant]) || 'span'

	const handleKeyDown = (e) => {
		if (e.which === 13) {
			Object.defineProperty(e, 'target', {
				writable: true,
				value: { value: e.target.innerHTML }
			})
			typeof onEnterKey === 'function' && onEnterKey(e)
		}
	}

	const handleChange = (e) => {
		Object.defineProperty(e, 'target', {
			writable: true,
			value: { value: e.target.innerHTML }
		})
		typeof onChange === 'function' && onChange(e)
	}

	const handleBlur = (e) => {
		Object.defineProperty(e, 'target', {
			writable: true,
			value: { value: e.target.innerHTML }
		})
		typeof onBlur === 'function' && onBlur(e)
	}

	return (
		<Component
			ref={ref}
			className={clsx(
				classes[weight],
				variant && stylesClasses(variant, '-', classes),
				classes[align],
				classes.colorStyles,
				{
					[classes.gutterBottom]: gutterBottom
				},
				className,
				wordBreak && classes.forceWordBreak
			)}
			onKeyDown={contentEditable ? handleKeyDown : undefined}
			onKeyUp={contentEditable ? handleChange : undefined}
			onBlur={contentEditable ? handleBlur : undefined}
			contentEditable={contentEditable}
			suppressContentEditableWarning={contentEditable}
			{...other}
		>
			{children}
		</Component>
	)
})

export default withTheme('Vb-typography')(Typography)

Typography.propTypes = {
	/**
	 * The content of the component.
	 */
	children: PropTypes.node,
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 */
	component: PropTypes.elementType,
	/**
	 * Color of the text in string format.
	 * Valid values: color-name, hex value, rgba,
	 * or one from the color variables list
	 */
	color: (props, propName, componentName) => {
		const value = props[propName]

		if (typeof value === 'undefined') {
			return null
		}
		if (typeof value !== 'string') {
			return new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): hex, color name, rgba, or one of [${Object.keys(
					COLORS
				).join(', ')}]`
			)
		}
		return null
	},
	/**
	 * If `true`, the text will be editable by user.
	 * @default false
	 */
	contentEditable: PropTypes.bool,
	/**
	 * If `true`, the text will have a bottom margin.
	 * @default false
	 */
	gutterBottom: PropTypes.bool,
	/**
	 * Callback function fired after the contentEditable element is focused out.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (string).
	 */
	onBlur: PropTypes.func,
	/**
	 * Callback function fired after the contentEditable element is changed.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (any).
	 */
	onChange: PropTypes.func,
	/**
	 * If `true`, the text will have a bottom margin.
	 * @default false
	 */
	paragraph: PropTypes.bool,
	/**
	 * Applies the typography variant.
	 * @default 'body'
	 */
	variant: (props, propName, componentName) => {
		const value = props[propName]

		if (typeof value === 'string') {
			return Object.keys(variantMapping).includes(value)
				? null
				: new Error(
					`${value} in prop ${propName} of ${componentName} component is not a valid value. Valid values: ${Object.keys(
						variantMapping
					).join(', ')}`
				)
		}

		if (typeof value === 'object') {
			let error = null

			const keys = Object.keys(value)

			keys.every((key) => {
				if (!mediaQueries[key]) {
					error = new Error(
						`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
							mediaQueries
						).join(', ')}`
					)
					return false
				}
				if (!Object.keys(variantMapping).includes(value[key])) {
					error = new Error(
						`${value} in prop ${propName}.${key} of ${componentName} component is not a valid value. Valid values: ${Object.keys(
							variantMapping
						).join(', ')}`
					)
					return false
				}

				return true
			})

			if (error) {
				return error
			}
		}

		return null
	},
	/**
	 * Applies the theme typography weight.
	 */
	weight: PropTypes.oneOf(['light', 'regular', 'medium']),
	/**
	 * Applies the theme typography align.
	 */
	align: PropTypes.oneOf(['left', 'center', 'right'])
}
